/* rr-analytics — medición propia de la web (docs/plan-de-medicion.md §2–§4, fases 5 y 6).
   Publica eventos en el canal de eventos de Shopify (Shopify.analytics.publish). Los recoge el
   píxel personalizado «RR · GA4» (scripts/pixel-rr-ga4.js), que los une a la visita del píxel de
   Google y los manda a GA4. Nada personal: nombres de elementos, rutas sin parámetros y tiempos.
   - rr_page: al cargar (tipo de página, plantilla, tipo de pieza, colección).
   - rr_scroll: 25, 50, 75, 90 y 100 % de la página, a partir del primer scroll.
   - rr_section_view: cada vez que una sección deja de verse tras 1 s o más a la vista (al menos
     la mitad de la sección o de la pantalla), con el tiempo; también al salir de la página.
   - rr_media_view: lo mismo con cada foto o vídeo de la galería de la ficha (la mitad a la vista).
   - rr_page_time: al salir, tiempo con la pestaña a la vista, scroll máximo y secciones vistas.
   - rr_nav_click: clic en un enlace de la propia web (zona, elemento, destino, posición, sección).
   - rr_outbound: clic hacia otra web (red social u otra).
   - rr_menu: abre o cierra el menú del móvil, con el tiempo abierto.
   - rr_locale: abre el selector de país o idioma, o elige uno.
   - select_item y view_item_list: las piezas (data-rr-item) de cada lista (data-rr-list). Las
     colecciones las ve ya la app de Google (data-rr-list-app): ahí solo select_item.
   - rr_card_swipe: en el móvil, pasa las fotos de una tarjeta (la primera vez y al llegar a la última).
   - rr_<nombre>_view: un bloque con data-rr-view a la vista 1 s, una vez, con sus data-rr-p-*.
   - rr_gallery (zoom y paso en el visor), rr_fold_open, rr_size_guide, rr_checkout_click,
     rr_form_start y rr_form_error.
   ?rr_debug=1 enciende el modo de prueba en la sesión (consola y DebugView de GA4) aunque la
   medición esté apagada en snippets/analytics.liquid; ?rr_debug=0 lo apaga. ?rr_internal=1 marca
   este navegador como tráfico interno (lo quita el filtro de GA4) y su usuario con internal=yes;
   ?rr_internal=0 lo desmarca. Los robots evidentes no mandan nada (ver «bot» abajo).
   Los componentes mandan lo suyo con window.rrTrack(nombre, parámetros); lo que piden antes de
   que cargue este archivo espera en window.rrQ (snippets/analytics.liquid). */
(() => {
  const node = document.getElementById('rr-analytics-ctx');
  if (!node || (window.rrTrack && window.rrTrack.rr)) return;
  let ctx;
  try {
    ctx = JSON.parse(node.textContent);
  } catch (e) {
    return;
  }
  const query = new URLSearchParams(location.search);
  const flag = (param, key, local) => {
    try {
      const store = local ? localStorage : sessionStorage;
      if (query.has(param)) query.get(param) === '0' ? store.removeItem(key) : store.setItem(key, '1');
      return store.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  };
  const debug = flag('rr_debug', 'rr-debug', false);
  const internal = flag('rr_internal', 'rr-internal', true);
  if (!ctx.on && !debug) return;
  // Robots evidentes, fuera: navegador automatizado o la pantalla de 2000×2000 u 800×600 con la que el
  // revisor de anuncios de Meta y otros servidores cargan la página (25 oct 2026: 25 sesiones desde
  // Boardman y Ashburn, de 0 a 10 s). La visita de la app de Google la quita el panel (filtro de resolución).
  const bot = navigator.webdriver || [[2000, 2000], [800, 600]].some(([w, h]) => screen.width === w && screen.height === h);
  if (bot && !debug) return;

  // ---- Envío: cola hasta que Shopify tenga listo su canal de eventos ----
  const base = { page_type: ctx.pt, page_template: ctx.tpl, page_lang: ctx.lang, market: ctx.mk };
  const queue = [];
  let sent = 0;
  let timer = 0;
  const flush = () => {
    const a = window.Shopify && window.Shopify.analytics;
    if (!a || !a.publish) return false;
    while (queue.length) {
      const [name, data] = queue.shift();
      try {
        a.publish(name, data);
      } catch (e) {}
    }
    return true;
  };
  const track = (name, params) => {
    if (++sent > 200) return; // tope por página: nunca en bucle
    const data = Object.assign({}, base, params);
    if (debug) data.debug_mode = 1;
    // Interno: el evento (lo quita el filtro de GA4) y el usuario (propiedad internal, que marca también lo que
    // manda la app de Google con el mismo _ga y deja quitarlo en el panel).
    if (internal) {
      data.traffic_type = 'internal';
      data.user = Object.assign({}, data.user, { internal: 'yes' });
    }
    for (const k in data) if (data[k] === undefined || data[k] === null || data[k] === '') delete data[k];
    if (debug) console.info('[rr-analytics]', name, JSON.stringify(data));
    queue.push([name, data]);
    if (flush() || timer) return;
    let tries = 0;
    timer = setInterval(() => {
      if (flush() || ++tries > 40) {
        clearInterval(timer);
        timer = 0;
      }
    }, 250);
  };
  track.rr = 1;
  window.rrTrack = track;
  // lo pedido antes de cargar (avisos, la suscripción) y lo que llegue después por la cola
  const early = window.rrQ;
  window.rrQ = { push: (x) => track(x[0], x[1]) };
  if (Array.isArray(early)) early.forEach((x) => track(x[0], x[1]));

  // ---- Dónde está cada cosa ----
  const area = (n) =>
    n.closest('[data-rr-burger]')
      ? 'menu'
      : n.closest('.shopify-section-group-header-group')
        ? 'header'
        : n.closest('.rr-popup-section')
          ? 'popup'
          : n.closest('#shopify-section-cart-drawer')
            ? 'drawer'
            : n.closest('.rr-pdp__bar')
              ? 'sticky_bar'
              : n.closest('.rr-cookies')
                ? 'notice'
                : n.closest('.shopify-section-group-footer-group, footer')
                  ? 'footer'
                  : 'main';
  // La clave de la sección en la plantilla: shopify-section-template--123__hero → hero
  const sectionKey = (n) => {
    const s = n.closest('.shopify-section');
    return s ? s.id.replace('shopify-section-', '').split('__').pop() : undefined;
  };
  const ELEMENTS = [
    ['.rr-col__inter', 'interstitial'],
    ['.rr-header__logo', 'logo'],
    ['.rr-pdp__crumb', 'breadcrumb'],
    ['.rr-card, .rr-cardwrap', 'card'],
    ['.rr-mega', 'mega'],
    ['.rr-header__drawer-sublink', 'menu_sublink'],
    ['.rr-header__drawer-link', 'menu_link'],
    ['.rr-header__search', 'search'],
    ['.rr-header__cart', 'cart'],
    ['.rr-header__link', 'nav_link'],
    ['.rr-footer__legal-link', 'legal_link'],
    ['.rr-footer__link', 'footer_link'],
    ['.rr-tiles', 'tile'],
    ['.rr-btn', 'cta'],
  ];
  const element = (a) => a.dataset.rrEl || (ELEMENTS.find(([sel]) => a.closest(sel)) || [0, 'link'])[1];
  // Orden dentro de su grupo: la tarjeta en su cuadrícula; el enlace en su lista, menú o fila
  const position = (a, el) => {
    let item = a;
    let all;
    if (el === 'card') {
      // la pieza en su lista (en la colección editorial cada tarjeta va en su propio hueco)
      const card = a.closest('[data-rr-item]');
      const list = card && listOf(card);
      if (list) return itemsIn(list).indexOf(card) + 1 || undefined;
      item = a.closest('.rr-cardwrap') || a.closest('.rr-card');
      all = item && item.parentElement && [...item.parentElement.children].filter((c) => c.matches('.rr-cardwrap, .rr-card'));
    } else {
      const box = a.closest('ul, ol, [role="list"], .rr-tiles, .rr-header__links, .rr-header__drawer-menu');
      all = box && [...box.querySelectorAll('a[href]')];
    }
    const i = all ? all.indexOf(item) : -1;
    return i >= 0 ? i + 1 : undefined;
  };
  const LOCALE = /^\/(?:en|es|fr|de|it|pt)(?:-[a-z]{2})?(?=\/|$)/i;
  const route = (u) => {
    const p = u.pathname.replace(LOCALE, '') || '/';
    const type =
      p === '/'
        ? 'home'
        : /^\/(collections\/[^/]+\/)?products\//.test(p)
          ? 'product'
          : /^\/(collections|pages|policies|blogs|cart|search|account)\b/.test(p)
            ? { collections: 'collection', pages: 'page', policies: 'policy', blogs: 'blog' }[p.split('/')[1]] ||
              p.split('/')[1]
            : 'other';
    return [type, p.slice(0, 100)];
  };
  const NETWORKS = ['instagram', 'pinterest', 'tiktok', 'youtu', 'facebook', 'whatsapp', 'threads', 'twitter'];
  const network = (host) => {
    const n = NETWORKS.find((x) => host.includes(x));
    return n === 'youtu' ? 'youtube' : n || 'other';
  };

  // ---- Piezas y listas (formato de la app de Google: snippets/analytics-item.liquid) ----
  const itemOf = (el, index) => {
    try {
      const it = JSON.parse(el.dataset.rrItem);
      it.item_brand = 'Rebel Rules';
      if (index >= 0) it.index = index;
      return it;
    } catch (e) {
      return null;
    }
  };
  const listOf = (el) => el.closest('[data-rr-list]');
  const itemsIn = (list) => [...list.querySelectorAll('[data-rr-item]')].filter((c) => listOf(c) === list);
  const listInfo = (list) => ({
    item_list_id: list.dataset.rrList,
    item_list_name: list.dataset.rrListName || list.dataset.rrList,
    currency: ctx.cur,
  });
  // Filtros y orden de la colección: son enlaces con filter.* o sort_by. Qué cambia respecto a ahora.
  const facets = (u) => {
    const now = new URLSearchParams(location.search);
    const next = u.searchParams;
    if (next.has('sort_by') && next.get('sort_by') !== now.get('sort_by')) return ['rr_sort', { sort_by: next.get('sort_by') }];
    const keys = [...new Set([...now.keys(), ...next.keys()])].filter((k) => k.indexOf('filter.') === 0);
    if (keys.length && ![...next.keys()].some((k) => k.indexOf('filter.') === 0)) return ['rr_filter', { action: 'clear' }];
    for (const k of keys) {
      const a = now.getAll(k).join(',');
      const b = next.getAll(k).join(',');
      if (a === b) continue;
      const price = /\.price\./.test(k);
      const on = b.length > a.length;
      const value = price ? `${next.get('filter.v.price.gte') || ''}-${next.get('filter.v.price.lte') || ''}` : on ? b : a;
      return ['rr_filter', { action: on ? 'apply' : 'remove', filter_name: price ? 'price' : k.replace(/^filter\.[vp]\./, ''), filter_value: value.slice(0, 60) }];
    }
    return null;
  };
  // foto o vídeo de la galería de la ficha: su orden en el grupo que se enseña
  const tilesOf = (g) => [...g.children].filter((c) => c.classList.contains('rr-pdp__tile'));
  const mediaType = (t) => (t.matches('rr-video') || t.querySelector('video, rr-video') ? 'video' : 'image');

  // ---- Clics: navegación, salidas y selector de país o idioma ----
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const loc = t.closest('button[name="country_code"], button[name="locale_code"]');
      if (loc) {
        const kind = loc.name === 'country_code' ? 'country' : 'language';
        track('rr_locale', {
          action: 'change',
          kind,
          from: kind === 'country' ? ctx.country : ctx.lang,
          to: (loc.value || '').slice(0, 5),
          ui_area: area(loc),
        });
        return;
      }
      // galería de la ficha: abrir el visor y pasar de foto dentro de él (rr-zoom)
      const zoom = t.closest('[data-rr-zoom-src]');
      if (zoom) {
        track('rr_gallery', { action: 'zoom', media_index: tilesOf(zoom.parentElement).indexOf(zoom), media_type: 'image' });
        return;
      }
      if (t.closest('[data-rr-zoom-step]')) {
        track('rr_gallery', { action: 'next' });
        return;
      }
      const pay = t.closest('button[name="checkout"], a[href*="/checkout"]');
      if (pay) {
        track('rr_checkout_click', { ui_area: area(pay) });
        return;
      }
      const a = t.closest('a[href]');
      if (!a || 'rrNo' in a.dataset) return;
      let u;
      try {
        u = new URL(a.href, location.href);
      } catch (er) {
        return;
      }
      if (!/^https?:$/.test(u.protocol)) return;
      const zone = area(a);
      if (u.host !== location.host) {
        track('rr_outbound', { network: network(u.host), ui_area: zone, target: u.host, section_id: sectionKey(a) });
        return;
      }
      if (u.pathname === location.pathname && u.hash) return; // ancla en la misma página
      const card = a.closest('[data-rr-item]');
      if (card) {
        const list = listOf(card);
        const it = itemOf(card, list ? itemsIn(list).indexOf(card) : -1);
        if (it) track('select_item', Object.assign(list ? listInfo(list) : { currency: ctx.cur }, { items: [it] }));
      }
      const [type, path] = route(u);
      const facet = type === 'collection' && u.pathname === location.pathname && facets(u);
      if (facet) {
        track(facet[0], facet[1]);
        return;
      }
      const el = element(a);
      track('rr_nav_click', {
        ui_area: zone,
        element: el,
        label: a.dataset.rrLabel,
        target_type: type,
        target: path,
        position: position(a, el),
        section_id: sectionKey(a),
      });
    },
    { capture: true, passive: true },
  );

  // ---- Fotos de las tarjetas (26 oct 2026, Cesar: saber en qué piezas se paran en la colección sin
  // entrar en la ficha). En una pantalla táctil la tarjeta se desliza con el dedo (snippets/card-product);
  // con ratón no se mueve, así que esto es solo móvil y tableta. rr_card_swipe, como mucho dos veces por
  // tarjeta y página: «swipe» al dejar la primera foto y «end» al llegar a la última. Se mide donde se para
  // el carrusel (400 ms quieto), no cada foto por la que pasa; el scroll no sube, se escucha en captura.
  const cardSeen = new WeakMap();
  document.addEventListener(
    'scroll',
    (e) => {
      const tr = e.target;
      if (!(tr instanceof Element) || !tr.classList.contains('rr-card__track')) return;
      clearTimeout(tr.rrT);
      tr.rrT = setTimeout(() => {
        const card = tr.closest('[data-rr-item]');
        const n = tr.querySelectorAll('.rr-card__slide').length;
        const i = Math.round(tr.scrollLeft / Math.max(1, tr.clientWidth));
        if (!card || n < 2 || i < 1) return;
        const seen = cardSeen.get(card) || {};
        const acts = [!seen.swipe && 'swipe', i >= n - 1 && !seen.end && 'end'].filter(Boolean);
        if (!acts.length) return;
        const list = listOf(card);
        const it = itemOf(card, -1);
        if (!it) return;
        for (const action of acts) {
          seen[action] = 1;
          track('rr_card_swipe', {
            action,
            piece: it.item_name,
            product_type: it.item_category2,
            media_index: i,
            position: list ? itemsIn(list).indexOf(card) + 1 : undefined,
            collection: ctx.pt === 'collection' ? ctx.coll : undefined,
            section_id: sectionKey(card),
          });
        }
        cardSeen.set(card, seen);
      }, 400);
    },
    { capture: true, passive: true },
  );

  // ---- Menú del móvil, selectores, filas plegables y guía de tallas ----
  // (toggle no sube: se escucha en captura; la guía es un popover y trae newState)
  let menuAt = 0;
  let guideAt = 0;
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (d instanceof HTMLElement && d.matches('[data-rr-guide]')) {
        const size_kind = d.dataset.rrGuide;
        if (e.newState === 'open') {
          guideAt = Date.now();
          track('rr_size_guide', { action: 'open', size_kind });
        } else if (guideAt) {
          track('rr_size_guide', { action: 'close', size_kind, open_ms: Date.now() - guideAt });
          guideAt = 0;
        }
        return;
      }
      if (!(d instanceof HTMLDetailsElement)) return;
      if (d.matches('[data-rr-fold]')) {
        if (d.open) track('rr_fold_open', { fold: d.dataset.rrFold, ui_area: area(d) });
        return;
      }
      if (d.matches('[data-rr-faq]')) {
        if (d.open) track('rr_faq_open', { faq_id: d.dataset.rrFaq });
        return;
      }
      if (d.matches('[data-rr-burger]')) {
        if (d.open) {
          menuAt = Date.now();
          track('rr_menu', { action: 'open' });
        } else if (menuAt) {
          track('rr_menu', { action: 'close', open_ms: Date.now() - menuAt });
          menuAt = 0;
        }
        return;
      }
      if (!d.open) return;
      const kind = d.querySelector('button[name="country_code"]')
        ? 'country'
        : d.querySelector('button[name="locale_code"]')
          ? 'language'
          : '';
      if (kind) track('rr_locale', { action: 'open', kind, ui_area: area(d) });
    },
    true,
  );

  // ---- Formularios de email: el origen sale de sus etiquetas (snippets/newsletter-tags) ----
  const formLoc = (f) => {
    const tags = f && f.querySelector('input[name="contact[tags]"]');
    if (!tags) return 'contact';
    const m = /(popup|footer|crew|prelaunch)/.exec(tags.value);
    return m ? m[1] : 'capture'; // el bloque email-capture fuera de la página Crew solo lleva «newsletter»
  };
  const started = new WeakSet();
  document.addEventListener(
    'input',
    (e) => {
      const f = e.target.form;
      if (!f || e.target.type !== 'email' || started.has(f)) return;
      started.add(f);
      track('rr_form_start', { form_location: formLoc(f), ui_area: area(f) });
    },
    true,
  );

  // ---- Stack: el carrito llega a un tramo (2, 3 o 4+ piezas), una vez por tramo y sesión ----
  // Aquí y no en cada escalera: puede haber varias en la misma página.
  const onCart = (e) => {
    const c = e.detail && e.detail.cart;
    if (!ctx.stack || !c || !Array.isArray(c.items)) return;
    const n = c.items.reduce((s, i) => s + (i.gift_card ? 0 : i.quantity), 0);
    const tier = n >= 4 ? 4 : n >= 3 ? 3 : n >= 2 ? 2 : 0;
    let max = 0;
    try {
      max = Number(sessionStorage.getItem('rr-stack-tier')) || 0;
      if (tier > max) sessionStorage.setItem('rr-stack-tier', String(tier));
    } catch (er) {}
    if (tier > max) track('rr_stack_tier', { stack_tier: tier, pieces: n });
  };
  document.addEventListener('rr:cart-updated', onCart);
  document.addEventListener('rr:cart-changed', onCart);

  // ---- Scroll ----
  const marks = [25, 50, 75, 90, 100];
  let scrollMax = 0;
  let ticking = false;
  addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const h = document.documentElement.scrollHeight;
        const d = h ? Math.min(100, Math.round(((scrollY + innerHeight) / h) * 100)) : 0;
        if (d <= scrollMax) return;
        scrollMax = d;
        while (marks.length && d >= marks[0]) track('rr_scroll', { percent: marks.shift() });
      });
    },
    { passive: true },
  );

  // ---- Velocidad real de la visita (rr_web_vitals, al salir, con rr_page_time) ----
  // LCP: el último candidato; CLS: la peor ventana de cambios, como web-vitals; INP: la interacción más
  // lenta (sin su percentil 98: con pocas interacciones da lo mismo); TTFB: la respuesta del documento.
  const vit = { lcp: 0, cls: 0, inp: 0 };
  let win = 0;
  let winFirst = 0;
  let winLast = 0;
  const observe = (type, fn, opts) => {
    try {
      new PerformanceObserver((l) => l.getEntries().forEach(fn)).observe(Object.assign({ type, buffered: true }, opts));
    } catch (e) {}
  };
  observe('largest-contentful-paint', (e) => (vit.lcp = e.startTime));
  observe('layout-shift', (e) => {
    if (e.hadRecentInput) return;
    if (e.startTime - winLast > 1000 || e.startTime - winFirst > 5000) {
      win = 0;
      winFirst = e.startTime;
    }
    win += e.value;
    winLast = e.startTime;
    vit.cls = Math.max(vit.cls, win);
  });
  observe('event', (e) => e.interactionId && (vit.inp = Math.max(vit.inp, e.duration)), { durationThreshold: 16 });
  const vitals = () => {
    const nav = performance.getEntriesByType('navigation')[0];
    track('rr_web_vitals', {
      lcp_ms: vit.lcp ? Math.round(vit.lcp) : undefined,
      cls: Math.round(vit.cls * 1000) / 1000,
      inp_ms: vit.inp ? Math.round(vit.inp) : undefined,
      ttfb_ms: nav ? Math.round(nav.responseStart) : undefined,
    });
  };

  // ---- Averías de nuestros componentes (rr_error): errores de los assets rr-*, 5 por página ----
  let fails = 0;
  addEventListener('error', (e) => {
    const m = /\/(rr-[a-z-]+)\.js/.exec(e.filename || '');
    if (!m || ++fails > 5) return;
    track('rr_error', { component: m[1], message: String(e.message || '').slice(0, 100) });
  });

  // ---- Tiempo: en la página, en cada sección y en cada foto de la galería ----
  const state = new Map();
  const seen = new Set();
  let activeMs = 0;
  let activeFrom = document.hidden ? 0 : performance.now();
  let timed = false;
  const endView = (s, now) => {
    const ms = Math.round(now - s.since);
    s.since = 0;
    if (ms < 1000) return;
    s.views++;
    if (s.sec) seen.add(s.p.section_id);
    track(s.ev, Object.assign({ visible_ms: ms, views: s.views }, s.p));
  };
  const leave = () => {
    const now = performance.now();
    if (activeFrom) activeMs += now - activeFrom;
    activeFrom = 0;
    state.forEach((s) => s.since && endView(s, now));
    if (timed) return;
    timed = true;
    track('rr_page_time', { active_ms: Math.round(activeMs), scroll_max: scrollMax, sections_seen: seen.size });
    vitals();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return leave();
    const now = performance.now();
    activeFrom = now;
    state.forEach((s) => {
      if (s.vis) s.since = now;
    });
  });
  addEventListener('pagehide', leave);

  const start = () => {
    const filtered = /[?&]filter\./.test(location.search);
    track('rr_page', {
      product_type: ctx.ptype,
      collection: ctx.coll,
      filtered: filtered ? 1 : undefined,
      results: filtered ? ctx.n : undefined,
      target: ctx.pt === '404' ? location.pathname.slice(0, 100) : undefined,
    });
    // búsqueda sin resultados: lo que piden y no tenemos (lo marca sections/search.liquid, en minúsculas;
    // el objeto search no llega al snippet del layout)
    const none = document.querySelector('[data-rr-search-empty]');
    if (none && none.dataset.rrSearchEmpty) track('rr_search_empty', { search_term: none.dataset.rrSearchEmpty });
    // un formulario de email que vuelve con error (todos lo pintan: manda el del ancla de la dirección)
    const errs = [...document.querySelectorAll('form .rr-field-error')];
    if (errs.length) {
      let f = errs[0].closest('form');
      try {
        const h = location.hash && document.querySelector(location.hash);
        const hf = h && (h.matches('form') ? h : h.querySelector('form'));
        if (hf && errs.some((x) => hf.contains(x))) f = hf;
      } catch (e) {}
      track('rr_form_error', { form_location: formLoc(f), error: 'invalid' });
    }
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        entries.forEach((en) => {
          const s = state.get(en.target);
          s.vis = s.media
            ? en.intersectionRatio >= 0.5
            : en.isIntersecting && en.intersectionRect.height >= Math.min(en.boundingClientRect.height, innerHeight) / 2;
          if (s.vis && !s.since && !document.hidden) s.since = now;
          else if (!s.vis && s.since) endView(s, now);
        });
      },
      { threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.3, 0.4, 0.5, 0.6, 0.75, 1] },
    );
    const measure = (el, s) => {
      state.set(el, Object.assign({ since: 0, views: 0, vis: false }, s));
      io.observe(el);
    };
    // Fuera las que no se pintan (display: none): en la ficha hay doce canvas de reglas y solo sale el de
    // la pieza. Por altura no vale: las recomendaciones llegan vacías y se rellenan al acercarse.
    const shown = (el) => [...el.children].some((c) => c.getClientRects().length > 0);
    const list = [...document.querySelectorAll('main .shopify-section, .shopify-section-group-footer-group:not(.rr-popup-section)')];
    list
      .filter(shown)
      .forEach((el, i) => {
        measure(el, { ev: 'rr_section_view', sec: 1, p: { section_id: sectionKey(el), position: i + 1, ui_area: area(el) } });
      });
    // cada foto o vídeo de cada grupo de la galería (los de otra piedra van ocultos y no cuentan)
    document.querySelectorAll('.rr-pdp__gallery').forEach((g) => {
      tilesOf(g).forEach((t, i) => measure(t, { ev: 'rr_media_view', media: 1, p: { media_index: i, media_type: mediaType(t) } }));
    });

    // ---- Una vez, tras 1 s a la vista: listas de piezas y bloques con data-rr-view ----
    const once = new Map();
    const io1 = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          const w = once.get(en.target);
          if (!w) return;
          clearTimeout(w.t);
          const r = en.boundingClientRect;
          if (!(en.isIntersecting && r.height > 0 && en.intersectionRect.height >= Math.min(r.height, innerHeight) / 2)) return;
          // (una marca de 1 px, como el final de la cuadrícula, también cuenta: su mitad es medio píxel)
          const fire = (tries) => {
            if (w.fn() !== false) {
              io1.unobserve(en.target);
              once.delete(en.target);
            } else if (tries < 8) w.t = setTimeout(() => fire(tries + 1), 1500); // aún vacía (recomendaciones)
          };
          w.t = setTimeout(() => fire(0), 1000);
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    const watch = (el, fn) => {
      once.set(el, { fn, t: 0 });
      io1.observe(el);
    };
    document.querySelectorAll('[data-rr-list]:not([data-rr-list-app])').forEach((l) =>
      watch(l, () => {
        const items = itemsIn(l)
          .slice(0, 20)
          .map((c, i) => itemOf(c, i))
          .filter(Boolean);
        if (!items.length) return false;
        track('view_item_list', Object.assign(listInfo(l), { items }));
      }),
    );
    // data-rr-p-eta-days="21" → eta_days: 21. El evento es rr_<data-rr-view>_view, o data-rr-event.
    document.querySelectorAll('[data-rr-view]').forEach((el) =>
      watch(el, () => {
        const p = { ui_area: area(el) };
        for (const k in el.dataset) {
          if (!/^rrP[A-Z]/.test(k)) continue;
          const v = el.dataset[k];
          p[k.slice(3).replace(/[A-Z]/g, (c, i) => (i ? '_' : '') + c.toLowerCase())] = /^-?\d+(\.\d+)?$/.test(v) ? +v : v;
        }
        track(el.dataset.rrEvent || 'rr_' + el.dataset.rrView + '_view', p);
      }),
    );
  };
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
  else setTimeout(start, 1200);
})();
